// ------------------------------------------------------------------------------
// PhoneLogin
// author: mudas
// created: 2025/3/30
// ------------------------------------------------------------------------------

import React, { FC, useRef, useState } from 'react';
import { useLocalStorageState, useResponsive } from 'ahooks';
import { LoginPayload, useAntStaticComponent, useFormHelper, useLoginHelper, usePhoneCode } from 'common/hooks';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { isPhoneNumberReg } from 'common/utils/validator';

import { LocalStorageKey, TiangongThemeType, VerCodeSendStatus } from 'common/types';
import { DEFAULT_REGION, DEFAULT_SHORT } from 'common/constants';

import { Button, ConfigProvider, Form, Input, Modal } from '@fuxi/eevee-ui';
import { GradientButton, SecondaryButton } from 'common/components';
import SelectCountry from '../SelectCountry';
import FormProtocol from '../FormProtocol';
import LoginButton from '../LoginButton';

import cx from './index.module.css';

export interface IProps {
  // 当前使用的主题类型
  theme?: TiangongThemeType;
  onSuccess?: (value: LoginPayload) => void;
}

const height = 48;
const borderRadius = 30;

const PhoneLogin: FC<IProps> = ({ theme, onSuccess }) => {
  const [form] = Form.useForm();
  const { getFieldValue, setFieldValue, validateFields, resetFields } = useFormHelper(form);

  const { loading, login } = useLoginHelper({ onSuccess });
  const { running, countdown, sendCode } = usePhoneCode();
  const { message: AntdMessage } = useAntStaticComponent();

  const responsive = useResponsive();

  const [region, setRegion] = useLocalStorageState<string>(LocalStorageKey.TiangongRegion, {
    defaultValue: DEFAULT_REGION,
  });
  const [phone, setPhone] = useLocalStorageState(LocalStorageKey.TiangongPhone, { defaultValue: '' });
  const [short, setShort] = useLocalStorageState(LocalStorageKey.TiangongShort, { defaultValue: DEFAULT_SHORT });

  const [isSmsDisabled, setIsSmsDisabled] = useState(!phone);
  const [isReadProtocolModal, setIsReadProtocolModal] = useState(false);

  const loginWrapperRef = useRef<any>();
  const codeBtnText = !running ? '获取验证码' : '重新获取' + (running ? `(${Math.round(countdown / 1000)})` : '');

  // --------------------------------------------------------------------------
  //
  // Class methods
  //
  // --------------------------------------------------------------------------

  const validateCountryPhone = (_, value) => {
    const short = getFieldValue('short');

    if (short === DEFAULT_SHORT) {
      // 境内手机号libphonenumber校验位数不准确，自定义reg校验
      const result = isPhoneNumberReg.test(value);

      if (result) {
        setIsSmsDisabled(false);
        setPhone(value);
        return Promise.resolve();
      } else {
        setIsSmsDisabled(true);
      }
    } else {
      // validate phone
      const result = isValidPhoneNumber(value, short);

      if (result) {
        setIsSmsDisabled(false);
        setPhone(value);
        return Promise.resolve();
      } else {
        setIsSmsDisabled(true);
      }
    }

    //if phone is invalid, return error
    return Promise.reject('请输入合法手机号');
  };

  // --------------------------------------------------------------------------
  //
  // Event handlers
  //
  // --------------------------------------------------------------------------

  const handleSendCode = async () => {
    try {
      const data = await validateFields(['phone']);
      const region = getFieldValue('region');
      const agreement = getFieldValue('agreement');

      if (!agreement) {
        // 显示阅读协议提示
        setIsReadProtocolModal(true);
        return;
      }

      const { status, msg } = await sendCode({ region, ...data });
      if (status === VerCodeSendStatus.TooFrequent) {
        AntdMessage.warning(msg);
        return;
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = async () => {
    const { agreement, phone, code } = await validateFields();
    const region = getFieldValue('region');

    login({
      phone: phone.trim(),
      code: code.trim(),
      region,
    });
  };

  const onAgreementChange = (e) => {
    const val = e.target.checked;
    setFieldValue('agreement', val);

    // 若code值为空，勾选后不必提示输入验证码。
    const codeValue = getFieldValue('code');
    if (val && !codeValue) {
      resetFields(['code']);
    }
  };

  const onCountrySelect = (value) => {
    setFieldValue('short', value.iso2.toUpperCase());
    // console.log('value.iso2.toUpperCase()', value.iso2.toUpperCase());
    setRegion(value.dialCode);
    setShort(value.iso2.toUpperCase());
    setFieldValue('region', value.dialCode);

    validateFields(['phone']);
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Form: {
            controlHeight: 48,
            labelHeight: 48,
          },
          Input: {
            controlHeight: height,
            borderRadius: borderRadius,
            colorBorder: 'rgba(255, 255, 255, 0.1)',
            controlHeightLG: 72,
            borderRadiusLG: 72,
            inputFontSize: 14,
            inputFontSizeLG: 21,
            fontSize: 14,
            fontSizeLG: 21,
          },
          InputNumber: {
            controlHeight: height,
            borderRadius: borderRadius,
            colorBorder: 'rgba(255, 255, 255, 0.1)',
            controlHeightLG: 72,
            borderRadiusLG: 72,

            inputFontSize: 14,
            inputFontSizeLG: 21,
            fontSize: 14,
            fontSizeLG: 21,
          },
          Button: {
            contentFontSize: 14,
            contentFontSizeLG: 21,
          },
          Select: {
            controlHeight: height,

            controlHeightLG: 72,

            fontSize: 14,
            fontSizeLG: 21,
            optionFontSize: 14,
            singleItemHeightLG: 72,
          },
        },
      }}>
      <div className={cx('mobile')} ref={loginWrapperRef}>
        <Form
          disabled={loading}
          className={cx('form', responsive.xl && 'form-large')}
          form={form}
          onFinish={handleSubmit}
          initialValues={{ short, region, code: '', phone }}>
          <Form.Item
            name="phone"
            extra={'手机号首次登录默认注册'}
            validateDebounce={200}
            rules={[
              {
                validator: validateCountryPhone,
              },
            ]}>
            <Input
              size={responsive.xl ? 'large' : 'middle'}
              addonBefore={<SelectCountry onSelect={onCountrySelect} value={region} />}
              placeholder="请输入手机号"
              allowClear
            />
          </Form.Item>
          <Form.Item
            name="code"
            rules={[
              {
                validator: (_, value) => {
                  const checked = getFieldValue('agreement');
                  if (!checked) {
                    return checked ? Promise.resolve() : Promise.reject(new Error('请先勾选下方协议'));
                  }
                  return value ? Promise.resolve() : Promise.reject('验证码不能为空');
                },
              },
            ]}>
            <Input
              size={responsive.xl ? 'large' : 'middle'}
              type="tel"
              className={cx('code-btn')}
              placeholder="请输入验证码"
              suffix={
                <Button
                  type="text"
                  size={responsive.xl ? 'middle' : 'small'}
                  shape="round"
                  className={`${running ? 'color-text-secondary' : ''}`}
                  disabled={loading || running || isSmsDisabled}
                  onClick={handleSendCode}>
                  {codeBtnText}
                </Button>
              }
            />
          </Form.Item>
          <Form.Item
            name="agreement"
            className={cx('form-item-protocol')}
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) => (value ? Promise.resolve() : Promise.reject(new Error('请勾选同意后再登录'))),
              },
            ]}>
            <FormProtocol onChange={onAgreementChange} />
          </Form.Item>

          <div className={cx('footer')}>
            <LoginButton loading={loading} onClick={handleSubmit}>
              登 录
            </LoginButton>
          </div>
        </Form>

        <Modal
          wrapClassName={cx('protocol-modal')}
          width={400}
          open={isReadProtocolModal}
          title="阅读协议和隐私政策等指引"
          footer={null}
          getContainer={() => loginWrapperRef.current}
          onCancel={() => setIsReadProtocolModal(false)}>
          <div className="mt-24px mb-24px">
            <FormProtocol readonly />{' '}
          </div>
          <div className={cx('protocol-modal-footer')}>
            <SecondaryButton className={cx('button')} onClick={() => setIsReadProtocolModal(false)}>
              取消
            </SecondaryButton>
            <GradientButton
              className={cx('button')}
              onClick={() => {
                onAgreementChange({ target: { checked: true } });
                setIsReadProtocolModal(false);
                handleSendCode();
              }}>
              同意并继续
            </GradientButton>
          </div>
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default PhoneLogin;
